import { Link } from 'react-router-dom';
import {
  Users,
  Award,
  AlertTriangle,
  Briefcase,
  Plus,
  UserPlus,
  CalendarDays,
  List,
  Plane,
  ListChecks,
  MapPin,
} from 'lucide-react';
import { Button } from '@/app/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/app/components/ui/card';
import { Badge } from '@/app/components/ui/badge';
import {
  useTechProfiles,
  useExpiringCerts,
  useExpiredCerts,
  useAssignments,
  useAvailability,
  useIncompleteTechsOnboarding,
  useOnSiteTechs,
} from '@/hooks/use-crew';
import { useOrgToday } from '@/hooks/use-settings';
import { usePermissions } from '@/hooks/use-permissions';
import { addLocalDays, formatDateOnly } from '@/lib/date-utils';

const certStatusColors: Record<string, string> = {
  valid: 'bg-green-100 text-green-800',
  expiring_soon: 'bg-yellow-100 text-yellow-800',
  expired: 'bg-red-100 text-red-800',
  revoked: 'bg-gray-100 text-gray-800',
};

export function CrewDashboardPage() {
  const orgToday = useOrgToday();
  const { canCreateTechs } = usePermissions();
  const { data: allTechs } = useTechProfiles({ page_size: 1 });
  const { data: activeTechs } = useTechProfiles({ page_size: 1, status: 'active' });
  const { data: expiredCerts } = useExpiredCerts();
  const { data: expiringCerts } = useExpiringCerts(60);
  const { data: activeAssignments } = useAssignments({ page_size: 1, status: 'active' });

  const weekEnd = addLocalDays(orgToday, 7);
  const { data: availableThisWeek } = useAvailability({
    date_from: orgToday,
    date_to: weekEnd,
    status: 'available',
    page_size: 50,
  });

  const summaryCards = [
    {
      title: 'Total Techs',
      value: allTechs?.count ?? '-',
      icon: Users,
      color: 'text-blue-600',
      bg: 'bg-blue-50',
      link: '/crew/techs',
    },
    {
      title: 'Active Techs',
      value: activeTechs?.count ?? '-',
      icon: Users,
      color: 'text-green-600',
      bg: 'bg-green-50',
      link: '/crew/techs?status=active',
    },
    {
      title: 'Expired Certs',
      value: expiredCerts?.count ?? '-',
      icon: AlertTriangle,
      color: 'text-red-600',
      bg: 'bg-red-50',
      link: '/crew/certifications/expiring',
    },
    {
      title: 'Certs Expiring Soon',
      value: expiringCerts?.count ?? '-',
      icon: AlertTriangle,
      color: 'text-orange-600',
      bg: 'bg-orange-50',
      link: '/crew/certifications/expiring',
    },
    {
      title: 'Currently Assigned',
      value: activeAssignments?.count ?? '-',
      icon: Briefcase,
      color: 'text-purple-600',
      bg: 'bg-purple-50',
      link: '/crew/techs',
    },
  ];

  const { data: incompleteTechs } = useIncompleteTechsOnboarding();
  const { data: onSiteTechs } = useOnSiteTechs();

  const quickActions = [
    ...(canCreateTechs ? [{ label: 'Add Tech', icon: UserPlus, to: '/crew/techs/new' }] : []),
    { label: 'View Techs', icon: Users, to: '/crew/techs' },
    { label: 'Travel', icon: Plane, to: '/crew/travel' },
    { label: 'Expiring Certs', icon: Award, to: '/crew/certifications/expiring' },
    { label: 'Crew Lists', icon: List, to: '/crew/lists' },
    { label: 'Onboarding', icon: ListChecks, to: '/crew/onboarding' },
    { label: 'Check-Ins', icon: MapPin, to: '/crew/check-ins' },
  ];

  return (
    <div className="p-4 sm:p-6 lg:p-8 space-y-6">
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1><img src="/logos/h3crew.svg" alt="H3Crew" className="h-7" /></h1>
          <p className="text-gray-600 mt-1">Workforce &amp; HR management</p>
        </div>
        {canCreateTechs && (
          <Button asChild>
            <Link to="/crew/techs/new">
              <UserPlus className="h-4 w-4 mr-2" />
              Add Technician
            </Link>
          </Button>
        )}
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-5 gap-4">
        {summaryCards.map((card) => (
          <Link key={card.title} to={card.link}>
            <Card className="hover:shadow-md transition-shadow cursor-pointer">
              <CardContent className="p-4">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm text-gray-600">{card.title}</p>
                    <p className="text-2xl font-bold mt-1">{card.value}</p>
                  </div>
                  <div className={`p-3 rounded-lg ${card.bg}`}>
                    <card.icon className={`h-6 w-6 ${card.color}`} />
                  </div>
                </div>
              </CardContent>
            </Card>
          </Link>
        ))}
      </div>

      {expiredCerts && (
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <AlertTriangle className="h-5 w-5 text-red-600" />
              Expired Certifications
            </CardTitle>
          </CardHeader>
          <CardContent>
            {expiredCerts.results.length > 0 ? (
              <div className="space-y-3">
                {expiredCerts.results.slice(0, 5).map((cert) => (
                  <Link
                    key={cert.id}
                    to={`/crew/techs/${cert.tech}`}
                    className="flex items-center justify-between rounded-lg bg-red-50 p-3 transition-colors hover:bg-red-100"
                  >
                    <div>
                      <p className="font-medium text-gray-900">{cert.cert_name}</p>
                      <p className="text-sm text-gray-600">
                        {cert.tech_name?.trim() || `Tech #${cert.tech}`}
                      </p>
                    </div>
                    <div className="flex items-center gap-2">
                      <Badge className={certStatusColors.expired}>
                        expired
                      </Badge>
                      <span className="text-sm text-gray-500">
                        Expired: {formatDateOnly(cert.expiry_date, 'N/A')}
                      </span>
                    </div>
                  </Link>
                ))}
                {expiredCerts.count > 5 && (
                  <Link
                    to="/crew/certifications/expiring"
                    className="block text-center text-sm text-blue-600 hover:underline"
                  >
                    View all {expiredCerts.count} expired certifications
                  </Link>
                )}
              </div>
            ) : (
              <p className="text-sm text-gray-500">No expired certifications.</p>
            )}
          </CardContent>
        </Card>
      )}

      {expiringCerts && expiringCerts.results.length > 0 && (
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <AlertTriangle className="h-5 w-5 text-orange-500" />
              Certifications Expiring Soon (Next 60 Days)
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-3">
              {expiringCerts.results.slice(0, 5).map((cert) => (
                <div
                  key={cert.id}
                  className="flex items-center justify-between p-3 bg-orange-50 rounded-lg"
                >
                  <div>
                    <p className="font-medium text-gray-900">{cert.cert_name}</p>
                    <p className="text-sm text-gray-600">
                      {cert.tech_name?.trim() || `Tech #${cert.tech}`}
                    </p>
                  </div>
                  <div className="flex items-center gap-2">
                    <Badge className={certStatusColors[cert.status] ?? 'bg-gray-100 text-gray-800'}>
                      {cert.status.replace('_', ' ')}
                    </Badge>
                    <span className="text-sm text-gray-500">
                      Expires: {formatDateOnly(cert.expiry_date, 'N/A')}
                    </span>
                  </div>
                </div>
              ))}
              {expiringCerts.count > 5 && (
                <Link
                  to="/crew/certifications/expiring"
                  className="text-sm text-blue-600 hover:underline block text-center"
                >
                  View all {expiringCerts.count} expiring certifications
                </Link>
              )}
            </div>
          </CardContent>
        </Card>
      )}

      {availableThisWeek && availableThisWeek.results.length > 0 && (
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <CalendarDays className="h-5 w-5 text-green-500" />
              Available This Week
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="flex flex-wrap gap-2">
              {availableThisWeek.results.slice(0, 10).map((entry) => (
                <Badge key={entry.id} variant="outline" className="py-1.5 px-3">
                  {entry.tech_name?.trim() || `Tech #${entry.tech}`} —{' '}
                  {formatDateOnly(entry.date)}
                </Badge>
              ))}
              {availableThisWeek.count > 10 && (
                <span className="text-sm text-gray-500 self-center">
                  +{availableThisWeek.count - 10} more
                </span>
              )}
            </div>
          </CardContent>
        </Card>
      )}

      {incompleteTechs && incompleteTechs.length > 0 && (
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <ListChecks className="h-5 w-5 text-orange-500" />
              Techs with Incomplete Onboarding
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-2">
              {incompleteTechs.slice(0, 5).map((t) => (
                <Link
                  key={`${t.tech_id}-${t.checklist_name}`}
                  to={`/crew/techs/${t.tech_id}`}
                  className="block"
                >
                  <div className="flex items-center justify-between p-3 bg-orange-50 rounded-lg hover:bg-orange-100 transition-colors">
                    <div>
                      <p className="font-medium text-gray-900">{t.tech_name}</p>
                      <p className="text-sm text-gray-600">{t.checklist_name}</p>
                    </div>
                    <Badge className="bg-orange-100 text-orange-800">
                      {t.status.replace('_', ' ')}
                    </Badge>
                  </div>
                </Link>
              ))}
              {incompleteTechs.length > 5 && (
                <Link
                  to="/crew/onboarding"
                  className="text-sm text-blue-600 hover:underline block text-center"
                >
                  View all {incompleteTechs.length} techs
                </Link>
              )}
            </div>
          </CardContent>
        </Card>
      )}

      {onSiteTechs && onSiteTechs.length > 0 && (
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <MapPin className="h-5 w-5 text-green-500" />
              Who's On Site Right Now ({onSiteTechs.length})
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="flex flex-wrap gap-2">
              {onSiteTechs.slice(0, 10).map((ci) => (
                <Badge key={ci.id} variant="outline" className="py-1.5 px-3">
                  <MapPin className="h-3 w-3 mr-1 text-green-500" />
                  {ci.tech_name}
                  {ci.location_name && ` — ${ci.location_name}`}
                </Badge>
              ))}
              {onSiteTechs.length > 10 && (
                <span className="text-sm text-gray-500 self-center">+{onSiteTechs.length - 10} more</span>
              )}
            </div>
            <Link
              to="/crew/check-ins"
              className="text-sm text-blue-600 hover:underline block mt-3"
            >
              View map &amp; all check-ins
            </Link>
          </CardContent>
        </Card>
      )}

      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Plus className="h-5 w-5" />
            Quick Actions
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
            {quickActions.map((action) => (
              <Button
                key={action.label}
                variant="outline"
                className="h-auto py-4 flex flex-col items-center gap-2"
                asChild
              >
                <Link to={action.to}>
                  <action.icon className="h-5 w-5" />
                  <span className="text-sm">{action.label}</span>
                </Link>
              </Button>
            ))}
          </div>
        </CardContent>
      </Card>
    </div>
  );
}
